import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  CheckCircle,
  Loader2,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

function redirectToCheckout(url) {
  const nextUrl = String(url || '').trim();
  if (!nextUrl) return;
  // Stripe checkout must run in a top-level browsing context.
  if (window.top && window.top !== window.self) {
    try {
      window.top.location.href = nextUrl;
      return;
    } catch (_) {
      const openedTop = window.open(nextUrl, '_top', 'noopener,noreferrer');
      if (openedTop) return;
      window.open(nextUrl, '_blank', 'noopener,noreferrer');
      return;
    }
  }
  window.location.assign(nextUrl);
}

export default function PaymentPage() {
  const { restaurantId, bookingId } = useParams();
  const params = new URLSearchParams(window.location.search);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookingInfo();
  }, [bookingId]);

  useEffect(() => {
    if (params.get('payment') === 'cancelled') {
      toast.error('Payment was not completed. Please try again to confirm your booking.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchBookingInfo = async () => {
    try {
      const response = await axios.get(`${API}/booking/${bookingId}/payment-info`);
      setBooking(response.data.booking);
      setRestaurant(response.data.restaurant);
      
      // Check if already paid
      if (response.data.booking.deposit_status === 'paid') {
        setError('already_paid');
      }
    } catch (error) {
      setError(error.response?.data?.detail === 'booking_expired' ? 'expired' : 'not_found');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-session?booking_id=${bookingId}&origin_url=${encodeURIComponent(originUrl)}`
      );
      redirectToCheckout(response.data.checkout_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
      setProcessing(false);
    }
  };

  const primaryColor = restaurant?.branding?.primary_color || '#1a1f2c';
  const currencySymbol = CURRENCY_SYMBOLS[booking?.deposit_currency || 'gbp'];
  const deductTransactionFees = Boolean(restaurant?.deposit_settings?.deduct_transaction_fees_from_refunds);
  const refundHours = booking?.deposit_refund_hours_before ?? 24;
  const renderedTerms = restaurant?.deposit_settings?.terms_and_conditions
    ?.replaceAll('{refund_hours}', String(refundHours))
    ?.replaceAll(
      '{refund_fee_policy}',
      deductTransactionFees
        ? 'Deposit refunded minus the original card processing fee charged by Stripe'
        : 'Full refund of deposit'
    );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="font-heading text-xl font-semibold mb-2">Booking Not Found</h2>
            <p className="text-muted-foreground">
              This booking link may have expired or the booking has been cancelled.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h2 className="font-heading text-xl font-semibold mb-2">This Booking Didn't Go Through</h2>
            <p className="text-muted-foreground mb-6">
              The deposit for this reservation wasn't paid in time, so the table hold has been released.
              Please make a new booking to secure your table.
            </p>
            <Button asChild className="w-full h-11">
              <Link to={`/book/${restaurantId}`}>Start a New Booking</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'already_paid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div 
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="font-heading text-xl font-semibold mb-2">Booking Confirmed</h2>
            <p className="text-muted-foreground mb-4">
              {booking?.deposit_payment_method === 'manual_phone'
                ? 'This deposit was marked as paid over phone and your reservation is confirmed.'
                : 'The deposit for this booking has been received and your reservation is confirmed. Your confirmation email should arrive shortly.'}
            </p>
            <div className="bg-slate-50 rounded-lg p-4 text-left text-sm">
              <p><strong>Date:</strong> {booking?.booking_date}</p>
              <p><strong>Time:</strong> {booking?.booking_time}</p>
              <p><strong>Guests:</strong> {booking?.party_size}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="payment-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div 
            className="inline-flex h-14 w-14 rounded-xl items-center justify-center text-white font-bold text-xl mb-3"
            style={{ backgroundColor: primaryColor }}
          >
            {restaurant?.name?.[0] || 'R'}
          </div>
          <h1 className="font-heading text-2xl font-bold">{restaurant?.name}</h1>
          <p className="text-muted-foreground text-sm mt-1">Complete Your Reservation</p>
        </div>

        <Card className="shadow-lg border-border/50">
          <CardContent className="p-6 space-y-6">
            {/* Booking Summary */}
            <div className="space-y-3">
              <h2 className="font-heading font-semibold">Booking Details</h2>
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span>{booking?.booking_date}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{booking?.booking_time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{booking?.party_size} {booking?.party_size === 1 ? 'guest' : 'guests'}</span>
                </div>
              </div>
            </div>

            {/* Deposit Amount */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800">Deposit Required</span>
                </div>
                <span className="text-xl font-bold text-amber-900">
                  {currencySymbol}{booking?.deposit_amount?.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-2">
              <p className="font-medium text-slate-900">Deposit and cancellation terms</p>
              <p>
                Deposits help the restaurant protect table availability and reduce last-minute no-shows. Your
                booking is confirmed once the deposit is paid.
              </p>
              <p>
                If your booking is eligible for a refund and you cancel in time, your deposit will be returned
                {deductTransactionFees ? ' minus the original card processing fee charged by Stripe.' : ' in full.'}
                {' '}Late cancellations and no-shows may forfeit the deposit.
              </p>
              <p>
                <strong>Moved bookings:</strong> if your booking is moved at your request within {refundHours} hours
                of the original booking time, your deposit is carried over to the new booking and becomes
                non-refundable. It does not become refundable again, even if the new date is further away.
              </p>
              {renderedTerms && (
                <details className="pt-1">
                  <summary className="cursor-pointer font-medium text-slate-900">Full terms</summary>
                  <div className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs">
                    {renderedTerms}
                  </div>
                </details>
              )}
            </div>

            {/* Pay Button */}
            <Button
              onClick={handlePayment}
              disabled={processing}
              className="w-full h-12 text-base"
              style={{ backgroundColor: primaryColor }}
              data-testid="pay-deposit-btn"
            >
              {processing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay {currencySymbol}{booking?.deposit_amount?.toFixed(2)} Deposit
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Secure payment powered by Stripe. Your booking will be confirmed once payment is complete.
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by <span className="font-semibold">DineSpot</span>
        </p>
      </div>
    </div>
  );
}
